import { ChevronDownIcon, ListFilterIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

export interface FacetOption {
  key: string
  label: string
  isChecked: boolean
  onToggle: () => void
}

export interface FacetGroup {
  key: string
  label: string
  options: FacetOption[]
  emptyLabel?: string
}

interface FacetFilterMenuProps {
  groups: FacetGroup[]
  activeCount: number
}

interface FacetGroupSectionProps {
  group: FacetGroup
  isLast: boolean
}

// Radix stänger menyn vid varje val – förhindrat här så att flera filter kan bockas i på en gång.
function keepMenuOpen(event: Event): void {
  event.preventDefault()
}

function FacetGroupSection({ group, isLast }: FacetGroupSectionProps): React.ReactElement {
  const isEmpty = group.options.length === 0
  const showEmptyLabel = isEmpty && group.emptyLabel !== undefined

  return (
    <>
      <DropdownMenuGroup>
        <DropdownMenuLabel>{group.label}</DropdownMenuLabel>
        {showEmptyLabel && <p className="px-2 py-1.5 text-sm text-muted-foreground">{group.emptyLabel}</p>}
        {group.options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.key}
            checked={option.isChecked}
            onSelect={keepMenuOpen}
            onCheckedChange={option.onToggle}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuGroup>
      {!isLast && <DropdownMenuSeparator />}
    </>
  )
}

export function FacetFilterMenu({ groups, activeCount }: FacetFilterMenuProps): React.ReactElement {
  const hasActiveFacets = activeCount > 0
  const triggerLabel = hasActiveFacets ? `Filter (${activeCount})` : 'Filter'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
        >
          <ListFilterIcon />
          {triggerLabel}
          <ChevronDownIcon className="opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-64"
      >
        {groups.map((group, index) => (
          <FacetGroupSection
            key={group.key}
            group={group}
            isLast={index === groups.length - 1}
          />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
